import { SimpleSpine } from '@/containers/common/SimpleSpine'
import { SymbolsAnimationFactory } from '@/containers/symbols/factory/SymbolsAnimationFactory'
import { SymbolType } from '@/containers/symbols/SymbolType'
import { IPoolable } from '@base/pool/IPoolable'
import { ITrackEntry } from 'pixi-spine'
import * as PIXI from 'pixi.js'
import { SymbolContainerData } from '@/modules'

export class SymbolSpineAnimation extends SimpleSpine implements IPoolable {
    protected readonly _factory: SymbolsAnimationFactory
    protected _symbolData: SymbolContainerData

    public constructor(factory: SymbolsAnimationFactory) {
        super(factory.getAnimationResource())
        this._factory = factory
    }

    private isHighPayIndex(index: number | undefined): boolean {
        return (
            index === SymbolType.HP_1 ||
            index === SymbolType.HP_2 ||
            index === SymbolType.HP_3 ||
            index === SymbolType.HP_4
        )
    }

    private isLowPayIndex(index: number | undefined): boolean {
        return (
            index === SymbolType.LP_1 ||
            index === SymbolType.LP_2 ||
            index === SymbolType.LP_3 ||
            index === SymbolType.LP_4 ||
            index === SymbolType.LP_5
        )
    }

    private hasAnimation(label: string): boolean {
        if (!label || !this.skeleton?.data) return false

        // spine-pixi-v8: use skeleton data lookup
        const animation = this.skeleton.data.findAnimation(label)
        return !!animation
    }

    public play(state: string, loop: boolean = false): ITrackEntry {
        let label = this._factory.getAnimationLabel(this._symbolData, state)
        const isLp = this.isLowPayIndex(this._symbolData?.index)

        if (!label) {
            if (this.isHighPayIndex(this._symbolData?.index)) {
                console.warn('[WIN_OUT_DEBUG][HP] play: missing label', {
                    state,
                    symbolIndex: this._symbolData?.index,
                })
            }
            return null
        }

        if (isLp && !this.hasAnimation(label) && (state === 'land' || state === 'landing') && label.endsWith('_in')) {
            const fallbackLabel = label.replace('_in', '_landing')
            if (this.hasAnimation(fallbackLabel)) {
                label = fallbackLabel
            }
        }

        if (this.hasAnimation(label)) {
            const track = this.state.setAnimation(0, label, loop)
            track.mixDuration = 0
            this.applyLandSpeedBySymbolType(track, state)
            return track
        }

        if (this.isHighPayIndex(this._symbolData?.index)) {
            console.warn('[WIN_OUT_DEBUG][HP] play: animation not found in skeleton', {
                state,
                label,
                symbolIndex: this._symbolData?.index,
            })
        }

        return null
    }

    public setMixin(animateFrom: string, animateTo: string, loop: boolean): ITrackEntry {
        const nextAnimation = this._factory.getAnimationLabel(this._symbolData, animateTo)

        this.stateData.setMix(animateFrom, nextAnimation, 1)
        const track: ITrackEntry = this.state.setAnimation(0, nextAnimation, loop)
        return track
    }

    public init(data: SymbolContainerData) {
        this._symbolData = data
    }

    public reset() {
        this._symbolData = null
        this.skeleton.setToSetupPose()
        this.state.clearTracks()
    }

    public get data() {
        return this._symbolData
    }

    public get pool() {
        return this._factory
    }

    public queue(state: string, loop: boolean = false): ITrackEntry {
        let label = this._factory.getAnimationLabel(this._symbolData, state)
        const isLp = this.isLowPayIndex(this._symbolData?.index)

        if (!label) {
            if (this.isHighPayIndex(this._symbolData?.index)) {
                console.warn('[WIN_OUT_DEBUG][HP] queue: missing label', {
                    state,
                    symbolIndex: this._symbolData?.index,
                })
            }
            return null
        }

        if (isLp && !this.hasAnimation(label) && (state === 'land' || state === 'landing') && label.endsWith('_in')) {
            const fallbackLabel = label.replace('_in', '_landing')
            if (this.hasAnimation(fallbackLabel)) {
                label = fallbackLabel
            }
        }

        if (this.hasAnimation(label)) {
            const track = this.state.addAnimation(0, label, loop, 0)
            this.applyLandSpeedBySymbolType(track, state)
            return track
        }

        if (this.isHighPayIndex(this._symbolData?.index)) {
            console.warn('[WIN_OUT_DEBUG][HP] queue: animation not found in skeleton', {
                state,
                label,
                symbolIndex: this._symbolData?.index,
            })
        }

        return null
    }

    public replaceInSlotContainer(name: string, container: PIXI.Container): PIXI.Container {
        container.rotation = Math.PI
        container.skew.y = Math.PI

        const slots = (this.skeleton?.data as any)?.slots as Array<{ name: string }> | undefined
        const slotContainers = (this as any)?.slotContainers as PIXI.Container[] | undefined

        if (!slots?.length || !slotContainers?.length) {
            console.warn('[SymbolSpineAnimation] replaceInSlotContainer skipped: slots/slotContainers unavailable', {
                name,
                hasSlots: !!slots?.length,
                hasSlotContainers: !!slotContainers?.length,
            })
            return null
        }

        const count = Math.min(slots.length, slotContainers.length)

        for (let i: number = 0; i < count; i++) {
            if (slots[i].name == name) {
                const slotContainer = slotContainers[i]

                slotContainer.removeChildren()
                if (container.parent) {
                    container.parent.removeChild(container)
                }

                slotContainer.addChild(container)

                return slotContainer
            }
        }
        return null
    }

    public stopOnLastFrame = () => {
        //@ts-ignore
        const lastFrame = this.state.tracks[0].animation.duration - 0.01
        this.state.tracks[0].trackTime = lastFrame
        this.state.update(0)
        this.state.apply(this.skeleton)
        this.state.clearTracks()
    }

    private applyLandSpeedBySymbolType(track: ITrackEntry, state: string): void {
        if (!track || !this._symbolData || state !== 'land') {
            return
        }

        if (this.isHighValueSymbol(this._symbolData.index)) {
            // 30% slower
            track.timeScale = 0.85
            return
        }

        if (this.isLowValueSymbol(this._symbolData.index)) {
            // 30% faster
            track.timeScale = 1.3
            return
        }
    }

    private isHighValueSymbol(index: number): boolean {
        return index >= SymbolType.HP_4 && index <= SymbolType.HP_1
    }

    private isLowValueSymbol(index: number): boolean {
        return index >= SymbolType.LP_5 && index <= SymbolType.LP_1
    }
}
